/**
 * The part of the stage a reader can actually see, and the camera arithmetic
 * that frames into it.
 *
 * The canvas spans the whole stage and the panes float over it, so "the view"
 * and "what is visible" are two different rectangles. Every camera move that
 * decides where something goes on screen — the fit, the follow, a centre on one
 * node, the ease toward a node picked from a list — asks this file for the
 * second one. It used to be arithmetic inline in three store actions, each
 * clamping every inset to 40% of the stage width: a guard against a pane wider
 * than a tiny stage, which at 1440px also refused 184 of the 760 pixels the
 * sidebar and the thread really cover, so the "centre" of the frame sat under
 * the thread's glass and a first-ring caption came out as "agnetic levitation".
 * The bottom edge was not counted at all, so on a phone the composer and the
 * node sheet hid the lower half of every fit.
 *
 * Pure, no DOM: the insets are measured elsewhere (App for the columns,
 * `canvas/fieldInset.ts` for the bottom band) and handed in as numbers.
 */

import { clamp } from './geometry'
import type { Bounds } from './layout'
import type { Op, Session, Viewport } from './types'

/** How many pixels of each stage edge sit under floating chrome. */
export interface Inset {
  left: number
  right: number
  bottom: number
}

/** A rectangle of the stage, in stage pixels. */
export interface Field {
  x0: number
  y0: number
  x1: number
  y1: number
}

/**
 * The narrowest (and shortest) field the camera will frame into.
 *
 * The degenerate-width guard, and the only one. Insets are honoured whole while
 * they leave at least this much of the stage; past that they are scaled down
 * together, so the camera centres on a sliver partly under glass rather than on
 * a negative width. 120 is below the smallest field a desktop window leaves
 * (the thread's floor beside the sidebar at 768px leaves about 129), so the
 * guard never bites on a layout App actually produces — it exists for the one
 * that has not been built yet.
 */
export const MIN_FIELD = 120

/**
 * How far inside the field a node must sit to count as seen. A dot on the very
 * edge of the glass, its caption clipped by it, is not a node anyone can read,
 * so "in view" means clear of the edge by roughly one caption's height.
 */
export const LOOK_MARGIN = 56

/** One axis's pair of insets, honoured whole while they leave `MIN_FIELD`. */
function sharePair(a: number, b: number, span: number): [number, number] {
  const lo = Math.max(0, a)
  const hi = Math.max(0, b)
  const want = lo + hi
  const room = Math.max(0, span - MIN_FIELD)
  if (want <= room) return [lo, hi]
  // Scaled together rather than one clipped before the other: the field keeps
  // its place between the two panes, it just stops being able to avoid them.
  const f = room / want
  return [lo * f, hi * f]
}

/** The visible field of a `w` × `h` stage under `inset`. */
export function visibleField(w: number, h: number, inset: Inset): Field {
  const [l, r] = sharePair(inset.left, inset.right, w)
  const [, b] = sharePair(0, inset.bottom, h)
  return { x0: l, y0: 0, x1: Math.max(l, w - r), y1: Math.max(0, h - b) }
}

/**
 * The field a Fit frames into: the visible field less `edge` pixels at its
 * right, where the tool dock stands.
 *
 * The dock floats over the field's right edge (ui/CanvasChrome.tsx), a fixed
 * size on screen at every zoom, so a fit has to leave it room in pixels. The
 * layout's caption allowance is in world units, and at a fitted zoom it came to
 * about the dock's width: the outermost claim on the right landed under the dock
 * at 1440 and on a phone, its caption hidden behind it. The 3D Fit holds the
 * same `edge` (`FIT_EDGE` in space.ts) inside every side, because an orbit swings
 * any side round to the dock; the flat picture does not turn, so only the right
 * gives it up, and the graph centres in what is left.
 *
 * A Fit's alone. The follow keeps its own headroom (`FOLLOW_EASE`), and the field
 * a follow, a look, a centring and the 3D picture's centre all read is still the
 * visible one, so nothing that centres on "the field" moves. Never narrower than
 * `MIN_FIELD`, the guard `visibleField` keeps: the edge gives way first.
 */
export function fitField(f: Field, edge: number): Field {
  const take = Math.max(0, Math.min(edge, f.x1 - f.x0 - MIN_FIELD))
  return { ...f, x1: f.x1 - take }
}

const fieldW = (f: Field) => Math.max(1, f.x1 - f.x0)
const fieldH = (f: Field) => Math.max(1, f.y1 - f.y0)

/** The camera that puts world point `p` at the field's centre, at zoom `k`. */
export function centerView(p: { x: number; y: number }, k: number, f: Field): Viewport {
  return { k, x: (f.x0 + f.x1) / 2 - p.x * k, y: (f.y0 + f.y1) / 2 - p.y * k }
}

/**
 * The camera that fits `b` inside the field, zoom clamped to `[kMin, kMax]` and
 * scaled by `ease` — below 1 to leave headroom, as the follow does.
 */
export function fitView(b: Bounds, f: Field, kMin: number, kMax: number, ease = 1): Viewport {
  const bw = Math.max(1, b.maxX - b.minX)
  const bh = Math.max(1, b.maxY - b.minY)
  const k = clamp(Math.min(fieldW(f) / bw, fieldH(f) / bh) * ease, kMin, kMax)
  return centerView({ x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 }, k, f)
}

/** The margin actually applied, shrunk so it can never close a small field. */
function marginFor(f: Field, margin: number): number {
  return Math.max(0, Math.min(margin, fieldW(f) / 4, fieldH(f) / 4))
}

/** Whether world point `p` lands inside the field, `margin` clear of its edges. */
export function inField(
  p: { x: number; y: number },
  v: Viewport,
  f: Field,
  margin = LOOK_MARGIN,
): boolean {
  const m = marginFor(f, margin)
  const sx = v.x + p.x * v.k
  const sy = v.y + p.y * v.k
  return sx >= f.x0 + m && sx <= f.x1 - m && sy >= f.y0 + m && sy <= f.y1 - m
}

/** The smallest pan of `v` that brings `p` inside the field. Zoom unchanged. */
export function keepInField(
  v: Viewport,
  p: { x: number; y: number },
  f: Field,
  margin = LOOK_MARGIN,
): Viewport {
  const m = marginFor(f, margin)
  const sx = v.x + p.x * v.k
  const sy = v.y + p.y * v.k
  const dx = sx < f.x0 + m ? f.x0 + m - sx : sx > f.x1 - m ? f.x1 - m - sx : 0
  const dy = sy < f.y0 + m ? f.y0 + m - sy : sy > f.y1 - m ? f.y1 - m - sy : 0
  return dx === 0 && dy === 0 ? v : { k: v.k, x: v.x + dx, y: v.y + dy }
}

/**
 * Whether the camera `v` already holds all of `b` inside the field, with a
 * slack of `slack` of the field on every side — the follow's dead zone.
 */
export function holdsBounds(b: Bounds, v: Viewport, f: Field, slack = 0.03): boolean {
  const vx0 = (f.x0 - v.x) / v.k
  const vx1 = (f.x1 - v.x) / v.k
  const vy0 = (f.y0 - v.y) / v.k
  const vy1 = (f.y1 - v.y) / v.k
  const sx = (vx1 - vx0) * slack
  const sy = (vy1 - vy0) * slack
  return b.minX >= vx0 + sx && b.maxX <= vx1 - sx && b.minY >= vy0 + sy && b.maxY <= vy1 - sy
}

/** The follow's zoom range and headroom. Below 0.3 a node is a smudge, so the
 *  outer branches run off the edge instead — the minimap and Fit are for that. */
export const FOLLOW_K_MIN = 0.3
export const FOLLOW_K_MAX = 1.1
export const FOLLOW_EASE = 0.87

/**
 * Where the auto-follow wants the camera, or null to leave it where it is.
 *
 * Dead zone first. The camera only moves when the graph is genuinely about to
 * leave the field — not on every arriving node. Re-fitting continuously is
 * technically correct framing and reads as the canvas sliding out from under
 * you, which is most of what made a growing graph feel chaotic. Past it, the
 * fit is pulled back (`FOLLOW_EASE`), so there is headroom for the next several
 * arrivals before the camera needs to move again.
 *
 * Then panned the least it takes to keep `keep` — the selected node — in the
 * field: when the graph is too big to fit at a legible zoom, a fit centred on
 * the bounds alone can put the node the reader is reading about under the
 * composer or the phone's sheet.
 */
export function followView(
  b: Bounds,
  current: Viewport,
  f: Field,
  keep: { x: number; y: number } | null = null,
): Viewport | null {
  if (holdsBounds(b, current, f)) return null
  return followFit(b, f, keep)
}

/** The follow's own frame: the fit it pulls back for headroom, the selection kept. */
function followFit(b: Bounds, f: Field, keep: { x: number; y: number } | null): Viewport {
  const fit = fitView(b, f, FOLLOW_K_MIN, FOLLOW_K_MAX, FOLLOW_EASE)
  return keep ? keepInField(fit, keep, f) : fit
}

/**
 * Whether `op` is the pass that opened the graph: aimed at the seed, it landed
 * something, and no pass before it had. A live Branch on a new question is one;
 * so is the worked example's unfurl, and a first run's first move.
 */
export function openedTheGraph(s: Pick<Session, 'nodes' | 'ops'>, op: Op): boolean {
  if (!op.produced.length) return false
  const target = op.targetIds.length === 1 ? s.nodes[op.targetIds[0]!] : undefined
  if (target?.parentId !== null) return false
  for (const o of s.ops) {
    if (o.id === op.id) return true
    if (o.produced.length) return false
  }
  return false
}

/**
 * Where the camera frames what the opening pass landed, or null when it already
 * does — the follow's own fit, without its dead zone, once.
 *
 * The dead zone moves the camera only when the graph is about to LEAVE the
 * field, so it only ever zooms out. A first ring landing into a view left zoomed
 * out — a new question typed after the worked example keeps its 0.3 — stayed
 * small in the middle of the field, half a phone's width across; and the worked
 * example's own opening, whose layout settles tighter than its widest moment,
 * ended at the follow's floor, a sixth smaller than the field had room for. The
 * first ring is the moment the question first pushes back, so when the opening
 * lands the camera frames what it landed: the same fit and headroom, the same
 * zoom range, the same selection kept, into the same visible field. It is still
 * a landing, so `follow` asks it only of a view nobody has pinned (invariant 11).
 *
 * A frame the camera is already at, to a pixel and a hundredth of a zoom step,
 * asks nothing: a target is what holds the loop at its full rate.
 */
export function openingView(
  b: Bounds,
  current: Viewport,
  f: Field,
  keep: { x: number; y: number } | null = null,
): Viewport | null {
  const next = followFit(b, f, keep)
  const same =
    Math.abs(Math.log(next.k / current.k)) < 0.01 &&
    Math.abs(next.x - current.x) < 1 &&
    Math.abs(next.y - current.y) < 1
  return same ? null : next
}

/**
 * Where to ease so a node picked from a list can be seen, or null when it
 * already can. Centred rather than nudged to the edge: a reader who chose a
 * node by name has no idea where it is, and the middle of the field is the one
 * place they will look. The zoom is left alone — that is theirs.
 */
export function lookView(
  p: { x: number; y: number },
  current: Viewport,
  f: Field,
  margin = LOOK_MARGIN,
): Viewport | null {
  return inField(p, current, f, margin) ? null : centerView(p, current.k, f)
}

/**
 * How much of the stage's bottom edge the given bands cover, in pixels.
 *
 * A band is chrome anchored along the bottom — the floating composer's strip,
 * the phone's node sheet — measured as a rectangle in the same coordinates as
 * the stage. Its cover is everything from its top edge down; an empty band
 * (zero height, as the strip is while the composer lives in the thread) covers
 * nothing. Never more than the stage itself.
 */
export function bottomCover(
  stage: { top: number; bottom: number },
  bands: ReadonlyArray<{ top: number; height: number; width: number }>,
): number {
  let cover = 0
  for (const b of bands) {
    if (b.height <= 0 || b.width <= 0) continue
    cover = Math.max(cover, stage.bottom - Math.max(stage.top, b.top))
  }
  return Math.round(Math.max(0, Math.min(stage.bottom - stage.top, cover)))
}
